import { Money } from '@insify/global-money';
import { groupBy } from 'lodash';

import type { Invoice } from '../types/Invoice';
import type { MoneyField } from '@insify/global-money';

export type InvoiceGroupByMonth = {
  month: string;
  total: MoneyField;
  invoices: Invoice[];
};

export type InvoiceGroupByYear = {
  year: string;
  months: InvoiceGroupByMonth[];
  invoices?: Invoice[];
};

const orderByYear = (a: InvoiceGroupByYear, b: InvoiceGroupByYear) => parseInt(b.year) - parseInt(a.year);
const orderGroupedInvoices = (invoices: InvoiceGroupByYear[]) => invoices.sort(orderByYear);

const groupInvoicesByYearAndMonth = (invoices: Invoice[]): InvoiceGroupByYear[] => {
  const groupedByYear = groupBy(invoices, (invoice) => invoice.issuedAtDate.getFullYear().toString());

  return Object.entries(groupedByYear).map(([year, invoicesOfYear]) => {
    const groupedByMonth = groupBy(invoicesOfYear, (invoice) => invoice.issuedAtDate.getMonth() + 1);
    const months: InvoiceGroupByMonth[] = Object.entries(groupedByMonth).map(([month, invoicesOfMonth]) => {
      const total = invoicesOfMonth.reduce(
        (sum, invoice) => sum.add(Money.fromField(invoice.totalAmount)),
        Money.EUR_ZERO,
      );
      return {
        month,
        total: total.toField(),
        invoices: invoicesOfMonth,
      };
    });

    return {
      year,
      months,
    };
  });
};

const groupAndOrderInvoices = (invoices: Invoice[]): InvoiceGroupByYear[] =>
  orderGroupedInvoices(groupInvoicesByYearAndMonth(invoices));

const getMonthNameByLocale = (month: string, locale: string) =>
  new Date(0, parseInt(month) - 1).toLocaleString(locale, { month: 'long' }).replace(/^\w/, (c) => c.toUpperCase());

export const InvoiceUtils = {
  groupInvoicesByYearAndMonth,
  orderGroupedInvoices,
  groupAndOrderInvoices,
  getMonthNameByLocale,
};
